import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Shield, User, Mail, Lock, Unlock, AlertTriangle, KeyRound, Copy, Eye, EyeOff, ClipboardList
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AdminLayout } from './AdminDashboard';
import { authAPI, surveyAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [wipeConfirmText, setWipeConfirmText] = useState('');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [surveysList, setSurveysList] = useState([]);
  const [selectedSurveyIds, setSelectedSurveyIds] = useState([]);
  const [savingAssign, setSavingAssign] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'hr_admin'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await authAPI.getAdminUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'hr_admin'
    });
  };

  const handleWipeData = async () => {
    if (wipeConfirmText !== 'DELETE ALL') {
      toast.error('Please type DELETE ALL to confirm');
      return;
    }
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/wipe-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('All data wiped successfully');
        setShowWipeDialog(false);
        setWipeConfirmText('');
        loadUsers();
      } else {
        toast.error(data.detail || 'Failed to wipe data');
      }
    } catch (error) {
      toast.error('Failed to wipe data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await authAPI.createAdmin(formData);
      toast.success('Admin user created successfully');
      setShowDialog(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser?.id) {
      toast.error('Cannot delete your own account');
      return;
    }

    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      await authAPI.deleteAdmin(userId);
      toast.success('User deactivated');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const openResetDialog = (user) => {
    setResetTarget(user);
    setResetPassword('');
    setShowResetPassword(false);
    setResetResult(null);
    setShowResetDialog(true);
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    try {
      const body = resetPassword.length >= 6 ? { new_password: resetPassword } : {};
      const response = await authAPI.resetUserPassword(resetTarget.id, body);
      setResetResult(response.data.temp_password);
      toast.success(`Password reset for ${resetTarget.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const copyPassword = (password) => {
    navigator.clipboard.writeText(password);
    toast.success('Password copied to clipboard');
  };

  const openAssignDialog = async (user) => {
    setAssignTarget(user);
    setSelectedSurveyIds(user.assigned_survey_ids || []);
    setShowAssignDialog(true);
    try {
      const res = await surveyAPI.getAll({ status: 'active' });
      setSurveysList(res.data || []);
    } catch (e) {
      toast.error('Failed to load surveys');
    }
  };

  const toggleSurveySelection = (surveyId) => {
    setSelectedSurveyIds((prev) =>
      prev.includes(surveyId) ? prev.filter((id) => id !== surveyId) : [...prev, surveyId]
    );
  };

  const handleSaveAssignments = async () => {
    if (!assignTarget) return;
    setSavingAssign(true);
    try {
      await authAPI.assignSurveys(assignTarget.id, selectedSurveyIds);
      toast.success(`${selectedSurveyIds.length} survey(s) assigned to ${assignTarget.name}`);
      setShowAssignDialog(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign surveys');
    } finally {
      setSavingAssign(false);
    }
  };

  const toggleRawDataAccess = async (user) => {
    const nextState = !user.raw_data_access;
    const confirmMsg = nextState
      ? `Enable RAW DATA ACCESS for ${user.name}?\n\nThis user will be able to see individual responses, participation list, and export raw data — bypassing the k=5 anonymity rule for assigned surveys.\n\nProceed only for trusted executive-level users.`
      : `Disable raw data access for ${user.name}?\n\nThe user will go back to anonymized aggregate view only.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await authAPI.setRawDataAccess(user.id, nextState);
      toast.success(nextState
        ? `Raw data access ENABLED for ${user.name}`
        : `Raw data access disabled for ${user.name}`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update raw data access');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">User Management</h1>
            <p className="text-muted-foreground">Manage admin users (Super Admin only)</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setShowDialog(true)} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Admin User
            </Button>
            <Button variant="destructive" onClick={() => setShowWipeDialog(true)} data-testid="wipe-data-btn">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Wipe All Data
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <Card data-testid="users-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {user.name?.charAt(0) || 'A'}
                            </span>
                          </div>
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={user.role === 'super_admin' ? 'default' : user.role === 'sr_management' ? 'outline' : 'secondary'}>
                            <Shield className="h-3 w-3 mr-1" />
                            {user.role === 'super_admin' ? 'Super Admin' : user.role === 'sr_management' ? 'Sr Management' : 'HR Admin'}
                          </Badge>
                          {user.role === 'sr_management' && (
                            <span className="text-xs text-muted-foreground" data-testid={`assigned-count-${user.id}`}>
                              {(user.assigned_survey_ids || []).length} survey(s) assigned
                            </span>
                          )}
                          {user.role === 'sr_management' && (
                            <span
                              className={`text-xs font-medium ${user.raw_data_access ? 'text-amber-700' : 'text-emerald-700'}`}
                              data-testid={`raw-access-status-${user.id}`}
                            >
                              {user.raw_data_access ? '⚠ Raw data access ON' : '🔒 Anonymized view'}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'outline' : 'destructive'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {user.id !== currentUser?.id && user.is_active && (
                          <div className="flex items-center justify-end gap-1">
                            {user.role === 'sr_management' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openAssignDialog(user)}
                                className="text-blue-600 hover:text-blue-700"
                                title="Assign Surveys"
                                data-testid={`assign-surveys-${user.id}`}
                              >
                                <ClipboardList className="h-4 w-4" />
                              </Button>
                            )}
                            {user.role === 'sr_management' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleRawDataAccess(user)}
                                className={user.raw_data_access ? 'text-amber-600 hover:text-amber-700' : 'text-emerald-600 hover:text-emerald-700'}
                                title={user.raw_data_access ? 'Disable raw data access (switch to anonymized view)' : 'Enable raw data access (see individual responses)'}
                                data-testid={`toggle-raw-access-${user.id}`}
                              >
                                {user.raw_data_access ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openResetDialog(user)}
                              className="text-amber-600 hover:text-amber-700"
                              title="Reset Password"
                              data-testid={`reset-password-${user.id}`}
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(user.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add User Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Add Admin User</DialogTitle>
              <DialogDescription>
                Create a new admin user for the HRMIS portal
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter full name"
                    className="pl-10"
                    data-testid="user-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                    className="pl-10"
                    data-testid="user-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="pl-10"
                    data-testid="user-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hr_admin">HR Admin</SelectItem>
                    <SelectItem value="sr_management">Sr Management (Read-only)</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="create-user-btn">
                  Create User
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign Surveys Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-blue-600" />
                Assign Surveys
              </DialogTitle>
              <DialogDescription>
                {assignTarget ? `Select which surveys ${assignTarget.name} (Sr Management) can access — analytics, responses & exports.` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 max-h-80 overflow-y-auto space-y-2" data-testid="assign-surveys-list">
              {surveysList.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No active surveys available</p>
              ) : (
                surveysList.map((survey) => (
                  <label
                    key={survey.id}
                    className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/40 transition-colors"
                    data-testid={`assign-survey-option-${survey.id}`}
                  >
                    <Checkbox
                      checked={selectedSurveyIds.includes(survey.id)}
                      onCheckedChange={() => toggleSurveySelection(survey.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{survey.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {survey.response_count || 0} response(s) • {survey.status === 'active' ? 'Active' : survey.status}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveAssignments} disabled={savingAssign} data-testid="save-assignments-btn">
                {savingAssign ? 'Saving...' : `Save (${selectedSurveyIds.length} selected)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Wipe Data Confirmation Dialog */}
        <Dialog open={showWipeDialog} onOpenChange={setShowWipeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Wipe All Data
              </DialogTitle>
              <DialogDescription>
                This will permanently delete ALL data including employees, surveys, responses, action items, and all admin users except the Super Admin.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">This action cannot be undone!</p>
                <p className="text-xs text-red-600 mt-1">All employees, surveys, responses, and admin users (except Super Admin) will be permanently deleted.</p>
              </div>
              <div>
                <Label className="text-sm">Type <span className="font-bold text-red-600">DELETE ALL</span> to confirm:</Label>
                <Input 
                  value={wipeConfirmText}
                  onChange={(e) => setWipeConfirmText(e.target.value)}
                  placeholder="Type DELETE ALL"
                  className="mt-2"
                  data-testid="wipe-confirm-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowWipeDialog(false); setWipeConfirmText(''); }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleWipeData}
                disabled={wipeConfirmText !== 'DELETE ALL'}
                data-testid="wipe-confirm-btn"
              >
                Wipe All Data
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manual Password Reset Dialog */}
        <Dialog open={showResetDialog} onOpenChange={(open) => { if (!open) { setShowResetDialog(false); setResetResult(null); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-amber-600" />
                Reset Password
              </DialogTitle>
              <DialogDescription>
                {resetTarget ? `Reset password for ${resetTarget.name} (${resetTarget.email})` : ''}
              </DialogDescription>
            </DialogHeader>

            {resetResult ? (
              <div className="space-y-4 py-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium">Password reset successfully!</p>
                  <p className="text-xs text-green-600 mt-1">Share this new password with the user securely.</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">New Password</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-muted rounded-lg px-4 py-3 font-mono text-sm select-all" data-testid="reset-result-password">
                      {resetResult}
                    </div>
                    <Button size="icon" variant="outline" onClick={() => copyPassword(resetResult)} data-testid="copy-password-btn">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => { setShowResetDialog(false); setResetResult(null); }} data-testid="reset-done-btn">
                    Done
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">You can either set a custom password or generate a random one.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-pw">New Password (optional)</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="reset-pw"
                      type={showResetPassword ? 'text' : 'password'}
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Leave empty to auto-generate"
                      className="pl-10 pr-10"
                      data-testid="manual-reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {resetPassword && resetPassword.length < 6 && (
                    <p className="text-xs text-red-500">Password must be at least 6 characters</p>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowResetDialog(false); setResetResult(null); }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleResetPassword}
                    disabled={resetPassword.length > 0 && resetPassword.length < 6}
                    data-testid="confirm-reset-password-btn"
                  >
                    {resetPassword.length >= 6 ? 'Set Password' : 'Generate Random Password'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
